import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { OverLayParameter } from '../../App';
import { ActivityHit, AlgoliaResponse, SiteHit } from '../../tools/algoliaTypes';
import { getItinerary, getNearby, getStoreInfo, putItinerary } from '../../tools/ciblerAPI';
import { RequirementsProps } from '../../tools/context';
import { makeAnAlgoliaSearch } from '../../tools/searchAPI';
import { cityRadiusMapping } from '../../tools/utils';
import { TravelPlannerConfigType } from '../TravelPlaner/types';
import { SitesExploratorSite } from './SitesExplorator';
import { TravelPlannerResultsConfigType } from './types';

const algoliaIndexes = ['tca_stores', 'tca_store_replica_reviewscore'];

type ContextType = {
  journey?: any;
  isLoadingJourney?: boolean;
  agentId?: string;
  setOverlay?: (p: OverLayParameter) => void;
  config?: TravelPlannerResultsConfigType;
  inputs?: any;
  setInputs?: React.Dispatch<React.SetStateAction<any>>;
  requirements?: RequirementsProps;
  trip?: any;
  setTrip?: React.Dispatch<React.SetStateAction<any>>;
  day?: string;
  setDay?: React.Dispatch<React.SetStateAction<ContextType['day']>>;
  selectedPart?: 'dayPoints' | 'restaurants' | 'lodgings';
  handleGoTo?: (anchor: ContextType['selectedPart']) => void;
  showAllSites?: boolean;
  setShowAllSites?: React.Dispatch<React.SetStateAction<ContextType['showAllSites']>>;
  showMobileMap?: boolean;
  setShowMobileMap?: React.Dispatch<React.SetStateAction<ContextType['showMobileMap']>>;
  focusedSite?: number | string;
  setFocusedSite?: React.Dispatch<React.SetStateAction<ContextType['focusedSite']>>;
  isLoadingSites?: boolean;
  searchData?: {
    term?: string;
    index?: (typeof algoliaIndexes)[number];
    filters?: string[];
    bounds?: number[];
  };
  setSearchData?: React.Dispatch<React.SetStateAction<ContextType['searchData']>>;
  formattedData?: any;
  selectedActivities?: { [key: string]: number }[];
  setSelectedActivities?: React.Dispatch<React.SetStateAction<ContextType['selectedActivities']>>;
  updateTrip?: (newData: any, newSelectedActivities: any) => void;
  searchSite?: (newData?: {
    [key in keyof ContextType['searchData']]?: ContextType['searchData'][key];
  }) => void;
  searchPaginateSite?: () => Promise<void>;
  sites?: SitesExploratorSite[];
  setSites?: (sites: SitesExploratorSite[]) => void;
  sitesInTrip?: { id: number | string; dayIndex: number }[];
  setSitesInTrip?: React.Dispatch<React.SetStateAction<ContextType['sitesInTrip']>>;
  maxSitesPages?: number;
  allSitesPage?: number;
  selectedBrands?: string[];
  setSelectedBrands?: React.Dispatch<React.SetStateAction<string[]>>;
  selectedAlgoliaIndex?: string;
  handleChangeAlgoliaIndex?: (index: string) => void;
  siteConfiguration?: any;
  restaurantsPerDay?: Record<string, any>;
  lodgingsPerDay?: Record<string, any>;
  storesInfo?: Record<string, any>;
  chatInputDefaultValue?: string;
  setChatInputDefaultValue?: React.Dispatch<React.SetStateAction<string>>;
  shouldOpenChat?: boolean;
  setShouldOpenChat?: React.Dispatch<React.SetStateAction<boolean>>;
  openChatWithMessage?: (message: string) => void;
  nightInfoVisible?: boolean;
  setNightInfoVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  spotDetail?: any | null;
  openSpotDetail?: (spot: any) => void;
  closeSpotDetail?: () => void;
  spotIframe?: { url: string; title?: string } | null;
  openSpotIframe?: (url: string, title?: string) => void;
  closeSpotIframe?: () => void;
};

function createFormattedTrip(formattedData: any, selectedActivities: any) {
  let trip = {};
  for (const dayId in selectedActivities) {
    if (!selectedActivities.hasOwnProperty(dayId)) continue;
    const dayData = formattedData?.[dayId];
    if (!dayData) continue;
    trip[dayId] = {};
    const activities = selectedActivities[dayId];
    for (const activityId in activities) {
      if (activities.hasOwnProperty(activityId)) {
        const activity = dayData.find((item: any) => item.key === activityId);
        if (activity) {
          trip[dayId][activityId] = activity.value;
        }
      }
    }
  }
  return trip;
}

// (0,0) is the placeholder _geoloc of records missing coordinates: never
// treat it as a real position or every such record matches every other one.
const hasRealCoords = (lat: any, lng: any): boolean =>
  typeof lat === 'number' && typeof lng === 'number' && !(lat === 0 && lng === 0);

const isSameSpot = (pp: any, site: { id: any; store_name?: string; lat?: number; lng?: number }): boolean => {
  if (pp?.id != null && site.id != null && String(pp.id) === String(site.id)) return true;
  const ppName = (pp?.store_name || pp?.name || '').toString().toLowerCase().trim();
  const siteName = (site.store_name || '').toString().toLowerCase().trim();
  if (siteName && ppName && siteName === ppName) return true;
  if (hasRealCoords(site.lat, site.lng) && hasRealCoords(pp?.latitude, pp?.longitude)) {
    const dLat = Math.abs(pp.latitude - site.lat);
    const dLng = Math.abs(pp.longitude - site.lng);
    if (dLat < 0.001 && dLng < 0.001) return true;
  }
  return false;
};

const stampSitesWithTrip = (
  sitesList: SitesExploratorSite[],
  formattedData: any,
  selectedActivities: any
): SitesExploratorSite[] => {
  const trip = createFormattedTrip(formattedData, selectedActivities);
  const days = Object.keys(trip);
  return sitesList.map((site) => {
    let dayIndex: number | undefined;
    let pointIndex: number | undefined;
    days.forEach((d, dIdx) => {
      if (dayIndex !== undefined) return;
      Object.keys(trip[d]).forEach((p, pIdx) => {
        if (dayIndex !== undefined) return;
        if (trip[d][p]?.some((pp: any) => isSameSpot(pp, site))) {
          dayIndex = dIdx;
          pointIndex = pIdx;
        }
      });
    });
    return { ...site, dayIndex, pointIndex };
  });
};

const algoliaResponseToSitesList = (
  response?: AlgoliaResponse,
  formattedData?: any,
  selectedActivities?: any
): SitesExploratorSite[] => {
  const uniqueIds = new Set();

  const mapped =
    response?.hits
      .filter((s) => {
        if (uniqueIds.has(s.id)) {
          return false;
        } else {
          uniqueIds.add(s.id);
          return true;
        }
      })
      .map((s) => {
        let result: any = {
          type: s.type || 'site',
          id: s.id,
          post_content: s.post_content,
          post_content_en: s.post_content_en,
          post_content_es: s.post_content_es,
          post_content_de: s.post_content_de,
          post_content_it: s.post_content_it,
          post_content_nl: s.post_content_nl,
          urlimg: s.urlimg,
          lat: s._geoloc.lat,
          lng: s._geoloc.lng,
          category: s.category,
          categ_str: s.categ_str,
          brand: s.brand,
        };

        switch (s.type) {
          case 'activity':
            const d = s as ActivityHit;
            result = {
              ...result,
              store_name: d.name,
              _categories: d._categories,
              tarif: d.tarif,
              price_range: d.price_range,
              site: d.site,
              start_date: d.start_date,
              end_date: d.end_date,
            } as SitesExploratorSite;
            break;
          default:
            const d2 = s as SiteHit;
            result = {
              ...result,
              formatted_address: d2.formatted_address,
              modalities: d2.modalities,
              free: d2.free,
              possible_fee: d2.possible_fee,
              store_name: d2.store_name,
              tca_categ: d2.tca_categ,
              shop_grade: d2.shop_grade,
              assigned_disabilities: d2.assigned_disabilities,
              hebergement_accessible_france: d2.hebergement_accessible_france,
              quality: d2.quality,
              tourinsoft_id: d2.tourinsoft_id,
              adr: d2.adr,
              brand: d2.brand,
              interests: d2.interests,
              services: d2.services,
              situations: d2.situations,
              state: d2.state,
              url: d2.url,
            } as SitesExploratorSite;
            break;
        }

        return result;
      }) || [];

  return stampSitesWithTrip(mapped, formattedData, selectedActivities);
};

export const TravelPlanerResultsContext = createContext<ContextType>({});

export function TravelPlanerResultsProvider({
  children,
  agentId,
  setOverlay,
  initialJourney,
  config,
  requirements,
  initialSites,
  siteConfiguration,
  language = 'fr',
  travelPlanner,
}: PropsWithChildren<{
  agentId?: string;
  setOverlay?: (p: OverLayParameter) => void;
  initialJourney?: any;
  config?: TravelPlannerResultsConfigType;
  requirements?: RequirementsProps;
  initialSites?: any;
  siteConfiguration?: any;
  language?: string;
  travelPlanner?: {
    [key: string]: any;
    content: {
      config: TravelPlannerConfigType;
    }[];
  };
}>) {
  const [day, setDay] = useState<string>();
  const [selectedPart, setSelectedPart] = useState<ContextType['selectedPart']>('dayPoints');
  const [showAllSites, setShowAllSites] = useState<ContextType['showAllSites']>(false);
  const [showMobileMap, setShowMobileMap] = useState<ContextType['showMobileMap']>(false);
  const [focusedSite, setFocusedSite] = useState<ContextType['focusedSite']>(-1);
  const [isLoadingSites, setIsLoadingSites] = useState<ContextType['isLoadingSites']>(false);
  const [searchData, setSearchData] = useState<ContextType['searchData']>({});
  const [selectedActivities, setSelectedActivities] = useState<ContextType['selectedActivities']>(
    () => {
      const initialTrip = initialJourney?.travelData?.trip || {};
      const next: Record<string, Record<string, number>> = {};
      Object.keys(initialTrip).forEach((dayKey) => {
        const keys = Object.keys(initialTrip[dayKey] || {});
        const sel: Record<string, number> = {};
        keys.forEach((key) => {
          sel[key] = 0;
        });
        next[dayKey] = sel;
      });
      return next as any;
    }
  );
  const [sites, setSites] = useState<SitesExploratorSite[]>([]);
  const [sitesInTrip, setSitesInTrip] = useState<ContextType['sitesInTrip']>();
  const [maxSitesPages, setMaxSitesPages] = useState<ContextType['maxSitesPages']>(
    initialSites?.data?.nbPages || 1
  );
  const [allSitesPage, setAllSitesPage] = useState<ContextType['allSitesPage']>(0);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedAlgoliaIndex, setSelectedAlgoliaIndex] = useState<string>('tca_stores');
  const [journey, setJourney] = useState<any>(initialJourney);
  const [trip, setTrip] = useState<any>(journey?.travelData?.trip || {});
  const [inputs, setInputs] = useState<any>(journey?.travelData?.inputs || {});
  const [isLoadingJourney, setIsLoadingJourney] = useState<boolean>(
    Object.keys(trip || {})?.length < inputs?.tripLength
  );
  const [restaurantsPerDay, setRestaurantsPerDay] = useState<any>([]);
  const [lodgingsPerDay, setLodgingsPerDay] = useState<any>([]);
  const [initialFetchDone, setInitialFetchDone] = useState<boolean>(false);
  const [storesInfo, setStoresInfo] = useState<Record<string, any>>({});
  const [chatInputDefaultValue, setChatInputDefaultValue] = useState<string>('');
  const [shouldOpenChat, setShouldOpenChat] = useState<boolean>(false);
  const [nightInfoVisible, setNightInfoVisible] = useState<boolean>(true);
  const [spotDetail, setSpotDetail] = useState<any | null>(null);
  const [spotIframe, setSpotIframe] = useState<{ url: string; title?: string } | null>(null);

  const formattedData = useMemo(() => {
    const next: Record<string, { key: string; value: any }[]> = {};
    Object.keys(trip || {}).forEach((dayKey) => {
      const keys = Object.keys(trip[dayKey] || {});
      next[dayKey] = keys.map((key) => ({ key, value: trip[dayKey][key] }));
    });
    return next;
  }, [trip]);

  useEffect(() => {
    setSelectedActivities(((prev: any) => {
      const next: Record<string, Record<string, number>> = {};
      Object.keys(trip || {}).forEach((dayKey) => {
        const keys = Object.keys(trip[dayKey] || {});
        const sel: Record<string, number> = {};
        keys.forEach((key) => {
          sel[key] = prev?.[dayKey]?.[key] ?? 0;
        });
        next[dayKey] = sel;
      });
      return next;
    }) as any);
  }, [trip]);

  const algoliaFilterString = useMemo(() => {
    if (config && config.algoliaFilters && config.algoliaFilters !== '') {
      const filters = config.algoliaFilters.split(';');
      return `NOT ${filters.join(' AND NOT ')}`;
    }
    return '';
  }, [config]);

  const getBarycenter = (travelData: any): { lat: number; lon: number } | null => {
    try {
      const data = Object.keys(travelData)?.map((key) => travelData[key]);
      if (!data.length) return null;

      const points = data.map((d: any) => d[0]);
      //const points = data[0];
      if (!points.length) return null;
      const latitudes = points.map((p: any) => p.latitude);
      const longitudes = points.map((p: any) => p.longitude);
      const minLat = Math.min(...latitudes);
      const maxLat = Math.max(...latitudes);
      const minLon = Math.min(...longitudes);
      const maxLon = Math.max(...longitudes);

      return {
        lat: (minLat + maxLat) / 2,
        lon: (minLon + maxLon) / 2,
      };
    } catch (e) {
      console.error('Error getting barycenter', e);
      return null;
    }
  };

  const getNearbyFromBarycenter = async (type: 'restaurant' | 'hotel') => {
    const citiesFilter = travelPlanner?.content?.[0]?.config?.favorites
      ?.find((f) => f.key === inputs?.cityKey)
      ?.citiesFilter?.join('|');

    const data = await Promise.all(
      Object.keys(trip).map(async (dayKey: string) => {
        const barycenter = getBarycenter(trip[dayKey] || {}) || {
          lat: inputs?.lat,
          lon: inputs?.lon,
        };
        const cities = inputs?.cityKey?.split('|')?.map((c) => c.trim());
        const city = cities?.[Object.keys(trip)?.findIndex((d) => d === dayKey)];
        const radius = city ? cityRadiusMapping[city] : undefined;

        return barycenter?.lat && barycenter?.lon
          ? await getNearby(
              requirements?.parameters?.[0],
              type,
              requirements,
              barycenter.lat,
              barycenter.lon,
              citiesFilter,
              radius
            )
          : [];
      })
    );

    return Object.keys(trip).reduce((acc: any, dayKey: string, index: number) => {
      acc[dayKey] = data[index];
      return acc;
    }, {});
  };

  // Initial fetch for restaurants and lodgings
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!initialFetchDone && Object.keys(trip || {}).length > 0) {
        if (config.showLodgings) {
          setLodgingsPerDay(await getNearbyFromBarycenter('hotel'));
        }
        if (config.showRestaurants) {
          setRestaurantsPerDay(await getNearbyFromBarycenter('restaurant'));
        }
        setInitialFetchDone(true);
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip, initialFetchDone, config.showLodgings, config.showRestaurants]);

  useEffect(() => {
    const isV3 = travelPlanner?.content?.[0]?.config?.botVersion === 'v3';
    if (!isV3) return setIsLoadingJourney(false);

    if (!isLoadingJourney) return;

    const citiesFilter =
      travelPlanner?.content?.[0]?.config?.favorites
        ?.find((f) => f.key === inputs?.cityKey)
        ?.citiesFilter?.join('|') || undefined;

    let intervalId: NodeJS.Timeout;
    let tmpData = journey;
    let retryCount = 0;
    const maxRetries = 5;

    const fetchJourney = () => {
      getItinerary(requirements?.parameters?.[0], requirements, language)
        .then(async (data) => {
          if (!data?.travelData || JSON.stringify(data) === JSON.stringify(tmpData)) {
            retryCount++;
            if (retryCount >= maxRetries) {
              clearInterval(intervalId);
              setIsLoadingJourney(false);
              return;
            }
            return;
          }

          retryCount = 0;
          const { travelData: results } = data;
          tmpData = data;

          const currentDays = Object.keys(trip || {});
          const newDays = Object.keys(results.trip || {}).filter(
            (day) => !currentDays.includes(day)
          );

          if (newDays.length > 0) {
            if (config.showLodgings) {
              const newLodgings = await Promise.all(
                newDays.map(async (dayKey: string) => {
                  const barycenter = getBarycenter(results.trip[dayKey] || {}) || {
                    lat: results.inputs?.lat,
                    lon: results.inputs?.lon,
                  };

                  return barycenter?.lat && barycenter?.lon
                    ? [
                        dayKey,
                        await getNearby(
                          requirements?.parameters?.[0],
                          'hotel',
                          requirements,
                          barycenter.lat,
                          barycenter.lon,
                          citiesFilter
                        ),
                      ]
                    : [dayKey, []];
                })
              );

              setLodgingsPerDay((prev) => {
                const updated = { ...prev };
                newLodgings.forEach(([day, lodgings]) => {
                  updated[day] = lodgings;
                });
                return updated;
              });
            }

            if (config.showRestaurants) {
              const newRestaurants = await Promise.all(
                newDays.map(async (dayKey: string) => {
                  const barycenter = getBarycenter(results.trip[dayKey] || {}) || {
                    lat: results.inputs?.lat,
                    lon: results.inputs?.lon,
                  };

                  return barycenter?.lat && barycenter?.lon
                    ? [
                        dayKey,
                        await getNearby(
                          requirements?.parameters?.[0],
                          'restaurant',
                          requirements,
                          barycenter.lat,
                          barycenter.lon,
                          citiesFilter
                        ),
                      ]
                    : [dayKey, []];
                })
              );

              setRestaurantsPerDay((prev) => {
                const updated = { ...prev };
                newRestaurants.forEach(([day, restaurants]) => {
                  updated[day] = restaurants;
                });
                return updated;
              });
            }
          }

          const tempResultsIntup = {
            ...results.inputs,
            search: results.inputs.search ? [results.inputs.search] : [],
            searchMap: results.inputs.searchMap ? [results.inputs.searchMap] : [],
          };
          setJourney(data);
          setTrip(results.trip);
          setInputs(tempResultsIntup);
          setIsLoadingJourney(Object.keys(results.trip || {}).length < results.inputs.tripLength);
        })
        .catch((e) => {
          console.error('Error loading journey', e);
          clearInterval(intervalId);
        });
    };

    intervalId = setInterval(fetchJourney, 2000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingJourney,
    requirements,
    language,
    journey,
    config.showLodgings,
    config.showRestaurants,
    trip,
    getNearby,
    getBarycenter,
  ]);

  // Stamping inputs kept in a ref so async search responses are stamped
  // against the live trip instead of the closure captured at request time.
  const stampInputsRef = useRef({ formattedData, selectedActivities });
  useEffect(() => {
    stampInputsRef.current = { formattedData, selectedActivities };
  });

  // selectedActivities is rebuilt from trip one commit after formattedData:
  // keying on it guarantees both are consistent when stamping sites.
  // After the user has searched, the initialSites hydration is disabled so a
  // search legitimately returning zero hits is never replaced by the initial list.
  const hasSearchedRef = useRef(false);
  useEffect(() => {
    if (!formattedData) return;
    setSites((prev) => {
      if (prev?.length) return stampSitesWithTrip(prev, formattedData, selectedActivities);
      if (hasSearchedRef.current || !initialSites?.data) return prev;
      return algoliaResponseToSitesList(
        initialSites.data as AlgoliaResponse,
        formattedData,
        selectedActivities
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActivities, initialSites]);

  useEffect(() => {
    setSitesInTrip(
      sites
        .filter((s) => typeof s.dayIndex === 'number' && typeof s.pointIndex === 'number')
        .map((s) => ({ id: s.id, dayIndex: s.dayIndex }))
    );
  }, [sites]);

  useEffect(() => {
    const fetchStoresInfoAllDays = async () => {
      const newStoresInfoAllDays: Record<string, any> = {};
      for (const dayKey in formattedData) {
        for (const pointKey in formattedData[dayKey]) {
          const pointData = formattedData[dayKey][pointKey];
          if (pointData.value?.[0]?.id) {
            newStoresInfoAllDays[pointData.value[0].id] = await getStoreInfo(
              pointData.value[0].id,
              requirements
            );
          }
        }
      }
      setStoresInfo(newStoresInfoAllDays);
    };

    fetchStoresInfoAllDays();
  }, [formattedData, requirements]);

  const contextValue = useMemo(() => {
    const updateTrip: ContextType['updateTrip'] = (newData, newSelectedActivities) => {
      const newTrip = {};
      Object.keys(newData).forEach((dayKey) => {
        const dayData = newData[dayKey];
        const day = {};
        Object.keys(dayData).forEach((pointKey) => {
          const pointData = dayData[pointKey];
          day[pointData.key] = pointData.value;
        });
        newTrip[dayKey] = day;
      });

      const oldDay = day;
      const oldDays = Object.keys(formattedData);
      const oldDayIndex = oldDays.indexOf(oldDay);
      const newDays = Object.keys(newTrip);
      const newDay =
        newDays[oldDayIndex] ||
        newDays[Math.max(0, Math.min(oldDayIndex, newDays.length - 1))] ||
        newDays[0];

      const tripLength = Object.keys(newTrip).length;

      setDay(newDay);
      setTrip(newTrip);
      setSelectedActivities(newSelectedActivities);
      setInputs((prev: any) => ({ ...prev, tripLength }));

      const { travelData: results } = journey;
      putItinerary(
        requirements.parameters[0],
        {
          ...results,
          inputs: {
            ...results.inputs,
            tripLength,
          },
          trip: newTrip,
        },
        requirements
      );
    };

    const searchSite: ContextType['searchSite'] = async (newData) => {
      hasSearchedRef.current = true;
      let data = { ...searchData };
      if (newData) {
        data = {
          ...data,
          ...newData,
        };
      }
      setSearchData(data);

      setIsLoadingSites(true);
      const customIndex = data.index || 'tca_stores';
      const { lat, lon, radius } = inputs || {};
      const isNoDefaultFilterPartner = ['huttopia', 'ccvba'].includes(
        requirements?.config?.key || ''
      );
      const isLargePagePartner = ['huttopia', 'ccvba'].includes(requirements?.config?.key || '');
      const isLargeRadiusPartner = ['huttopia', 'ccvba'].includes(requirements?.config?.key || '');
      const hitsPerPage = isLargePagePartner ? 1000 : 50;

      const newAlgoliaFilterString = !isNoDefaultFilterPartner
        ? data.filters?.length
          ? `${algoliaFilterString} AND (${data.filters?.join(' OR ')}) AND NOT categslug:ou-sejourner AND NOT categ_str:restaurant`
          : `${algoliaFilterString} AND NOT categslug:ou-sejourner AND NOT categ_str:restaurant AND NOT categslug:ou-sejourner AND NOT categslug:a-table AND NOT categslug:commerce`
        : data.filters?.length
          ? `${algoliaFilterString} AND (${data.filters?.join(' OR ')})`
          : algoliaFilterString;

      const response = await makeAnAlgoliaSearch(
        {
          query: data.term,
          radius: isLargeRadiusPartner ? 5000000000 : radius || 10000,
          index: customIndex,
          insideBoundingBox: data.bounds?.length % 4 === 0 ? data.bounds.toString() : undefined,
          hitsPerPage,
        },
        requirements,
        [],
        newAlgoliaFilterString,
        lat,
        lon
      );
      const newSites = algoliaResponseToSitesList(
        response.data as AlgoliaResponse,
        stampInputsRef.current.formattedData,
        stampInputsRef.current.selectedActivities
      );
      setMaxSitesPages(response.data.nbPages);
      setAllSitesPage(0);
      setIsLoadingSites(false);
      setSites(newSites);
    };

    const searchPaginateSite = async (): Promise<void> => {
      if (allSitesPage + 1 >= maxSitesPages) return;
      setIsLoadingSites(true);
      const customIndex = searchData.index || 'tca_stores';
      const { lat, lon, radius } = journey?.travelData || {};
      const isNoDefaultFilterPartner = ['huttopia', 'ccvba'].includes(
        requirements?.config?.key || ''
      );
      const isLargePagePartner = ['huttopia', 'ccvba'].includes(requirements?.config?.key || '');
      const isLargeRadiusPartner = ['huttopia', 'ccvba'].includes(requirements?.config?.key || '');
      const hitsPerPage = isLargePagePartner ? 1000 : 50;

      const newAlgoliaFilterString = !isNoDefaultFilterPartner
        ? searchData.filters?.length
          ? `${algoliaFilterString} AND ${searchData.filters?.join(' OR ')} AND NOT categslug:ou-sejourner AND NOT categ_str:restaurant`
          : `${algoliaFilterString} AND NOT categslug:ou-sejourner AND NOT categ_str:restaurant AND NOT categslug:ou-sejourner AND NOT categslug:a-table AND NOT categslug:commerce`
        : searchData.filters?.length
          ? `${algoliaFilterString} AND ${searchData.filters?.join(' OR ')}`
          : algoliaFilterString;

      makeAnAlgoliaSearch(
        {
          query: searchData.term,
          radius: isLargeRadiusPartner ? 5000000000 : radius || 10000,
          index: customIndex,
          page: allSitesPage + 1,
          hitsPerPage,
        },
        requirements,
        [],
        searchData.filters?.length
          ? `${newAlgoliaFilterString} AND ${searchData.filters?.join(' OR ')}`
          : newAlgoliaFilterString,
        lat,
        lon
      )
        .then(({ data }) => {
          const newSites = algoliaResponseToSitesList(
            data as AlgoliaResponse,
            stampInputsRef.current.formattedData,
            stampInputsRef.current.selectedActivities
          );
          setMaxSitesPages(data.nbPages);
          setAllSitesPage((p) => p + 1);
          setSites((s) => [...s, ...newSites] as SitesExploratorSite[]);
        })
        .catch((e) => console.error('Error paginating search', e))
        .finally(() => setIsLoadingSites(false));
    };

    const handleGoTo = (anchor: ContextType['selectedPart']) => {
      setSelectedPart(anchor);
      const elementTop =
        document.getElementById(anchor)?.getBoundingClientRect()?.top + window.scrollY;
      window.scrollTo({ top: elementTop - 110, behavior: 'smooth' });
    };

    const handleChangeAlgoliaIndex = (index: string) => {
      setSelectedAlgoliaIndex(index);
      searchSite({ index });
    };

    const openChatWithMessage = (message: string) => {
      setChatInputDefaultValue(message);
      setShouldOpenChat(true);
      // Reset after a short delay to allow the ChatAgent to consume the value
      setTimeout(() => {
        setShouldOpenChat(false);
      }, 100);
    };

    return {
      journey,
      agentId,
      setOverlay,
      config,
      inputs,
      setInputs,
      requirements,
      trip,
      setTrip,
      day,
      setDay,
      selectedPart,
      handleGoTo,
      showAllSites,
      setShowAllSites,
      showMobileMap,
      setShowMobileMap,
      focusedSite,
      setFocusedSite,
      isLoadingSites,
      searchData,
      setSearchData,
      formattedData,
      selectedActivities,
      setSelectedActivities,
      updateTrip,
      searchSite,
      searchPaginateSite,
      sites,
      setSites,
      sitesInTrip,
      setSitesInTrip,
      maxSitesPages,
      allSitesPage,
      selectedBrands,
      setSelectedBrands,
      selectedAlgoliaIndex,
      handleChangeAlgoliaIndex,
      siteConfiguration,
      restaurantsPerDay,
      lodgingsPerDay,
      storesInfo,
      chatInputDefaultValue,
      setChatInputDefaultValue,
      shouldOpenChat,
      setShouldOpenChat,
      openChatWithMessage,
      nightInfoVisible,
      setNightInfoVisible,
      spotDetail,
      openSpotDetail: (spot: any) => setSpotDetail(spot),
      closeSpotDetail: () => setSpotDetail(null),
      spotIframe,
      openSpotIframe: (url: string, title?: string) => setSpotIframe({ url, title }),
      closeSpotIframe: () => setSpotIframe(null),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    journey,
    agentId,
    setOverlay,
    config,
    inputs,
    requirements,
    trip,
    day,
    selectedPart,
    showAllSites,
    showMobileMap,
    focusedSite,
    isLoadingSites,
    searchData,
    formattedData,
    selectedActivities,
    sites,
    sitesInTrip,
    maxSitesPages,
    allSitesPage,
    selectedBrands,
    selectedAlgoliaIndex,
    siteConfiguration,
    restaurantsPerDay,
    lodgingsPerDay,
    storesInfo,
    chatInputDefaultValue,
    shouldOpenChat,
    nightInfoVisible,
    spotDetail,
    spotIframe,
  ]);

  return (
    <TravelPlanerResultsContext.Provider value={contextValue}>
      {children}
    </TravelPlanerResultsContext.Provider>
  );
}

export const useTravelPlannerResultsContext = () => {
  const context = useContext(TravelPlanerResultsContext);

  if (!context) {
    throw new Error(
      'useTravelPlannerResultsContext must be used within a TravelPlanerResultsProvider'
    );
  }

  return context;
};
